import {
  InlineEditChangeGroupEntry,
  InlineEditChangeFocusEventPayload,
  InlineEditModificationSource,
  TiptapOutputType,
} from '@amzn/awscat-editor';
import { Comment, InlineCommentClickHandler } from '@amzn/awscat-editor/dist/extensions/comment';
import { CommentPositionUpdate } from '../../../services/InlineCommentManager';
import { InlineTextFilter } from '../../../services/InlineTextOperations';
import { Editor } from '@tiptap/core';
import { PopoverState } from './Constants';
import { useInlineEditIntegration } from './useInlineEditIntegration';
import { useInlineCommentIntegration } from './useInlineCommentIntegration';
import { useAltTextEditing } from './useAltTextEditing';
import { useSubsectionIdEditing } from './useSubsectionIdEditing';
import { useCallback } from 'react';

export interface TiptapEditorEditingModeViewModelProps {
  /**
   * Indicates what inline comments and inline edits to load.
   * Pass `undefined` to load nothing.
   */
  readonly inlineTextFilter: InlineTextFilter | undefined;
  /** The Tiptap editor instance, owned by the view. */
  readonly editor: Editor | undefined;
}

export interface TiptapEditorEditingModeViewModel {
  readonly editorComments: Comment[] | undefined;
  readonly stageCommentPositions: (comments: CommentPositionUpdate[]) => void;
  readonly handleInlineCommentClick: InlineCommentClickHandler;
  readonly syncModifiedChanges: (
    entries: readonly InlineEditChangeGroupEntry[],
    source: InlineEditModificationSource,
  ) => void;
  readonly focusChange: (payload: InlineEditChangeFocusEventPayload) => void;
  readonly popover: PopoverState | undefined;
  readonly hidePopover: () => void;
  /** Whether the image alt-text editing modal is open. */
  readonly isAltTextModalVisible: boolean;
  /** Value to seed the alt-text modal with. */
  readonly initialAltTextValue: TiptapOutputType;
  /** Opens the alt-text modal for the image selected in the editor. */
  readonly openAltTextModal: (editor: Editor) => void;
  /** Saves the edited alt text back onto the image and closes the modal. */
  readonly saveAltTextValue: (value: TiptapOutputType) => void;
  /** Closes the alt-text modal without saving. */
  readonly dismissAltTextModal: () => void;
  /** Whether the heading subsection-ID editing modal is open. */
  readonly isSubsectionIdModalVisible: boolean;
  /** Value to seed the subsection-ID modal with. */
  readonly initialSubsectionIdValue: string;
  /** Opens the subsection-ID modal for the heading selected in the editor. */
  readonly openSubsectionIdModal: (editor: Editor) => void;
  /** Saves the edited subsection ID back onto the heading and closes the modal. */
  readonly saveSubsectionIdValue: (value: string) => void;
  /** Closes the subsection-ID modal without saving. */
  readonly dismissSubsectionIdModal: () => void;
  /** Copies the selected heading's `#fragment` anchor to the clipboard. */
  readonly copySubsectionAnchor: (editor: Editor) => void;
}

export function useTiptapEditorEditingModeViewModel(
  props: TiptapEditorEditingModeViewModelProps,
): TiptapEditorEditingModeViewModel {
  const inlineEditIntegration = useInlineEditIntegration(props.editor, props.inlineTextFilter);
  const inlineCommentIntegration = useInlineCommentIntegration(
    props.editor,
    props.inlineTextFilter,
  );
  const altTextEditing = useAltTextEditing(props.editor);
  const subsectionIdEditing = useSubsectionIdEditing(props.editor);
  // Combining hide-popover callbacks from both integrations
  const hideInlineCommentPopover = inlineCommentIntegration.hidePopover;
  const hideInlineEditPopover = inlineEditIntegration.hidePopover;
  const hideAllPopovers = useCallback(() => {
    hideInlineCommentPopover();
    hideInlineEditPopover();
  }, [hideInlineCommentPopover, hideInlineEditPopover]);

  return {
    editorComments: inlineCommentIntegration.editorComments,
    stageCommentPositions: inlineCommentIntegration.stageCommentPositions,
    handleInlineCommentClick: inlineCommentIntegration.handleInlineCommentClick,
    syncModifiedChanges: inlineEditIntegration.syncModifiedChanges,
    focusChange: inlineEditIntegration.focusChange,
    popover: inlineEditIntegration.popover ?? inlineCommentIntegration.popover ?? undefined,
    hidePopover: hideAllPopovers,
    isAltTextModalVisible: altTextEditing.isAltTextModalVisible,
    initialAltTextValue: altTextEditing.initialAltTextValue,
    openAltTextModal: altTextEditing.openAltTextModal,
    saveAltTextValue: altTextEditing.saveAltTextValue,
    dismissAltTextModal: altTextEditing.dismissAltTextModal,
    isSubsectionIdModalVisible: subsectionIdEditing.isSubsectionIdModalVisible,
    initialSubsectionIdValue: subsectionIdEditing.initialSubsectionIdValue,
    openSubsectionIdModal: subsectionIdEditing.openSubsectionIdModal,
    saveSubsectionIdValue: subsectionIdEditing.saveSubsectionIdValue,
    dismissSubsectionIdModal: subsectionIdEditing.dismissSubsectionIdModal,
    copySubsectionAnchor: subsectionIdEditing.copySubsectionAnchor,
  };
}
